import z from 'zod'
import { cn } from '@/helpers/utils'
import { OVERVIEW_CARD_CLASSNAME } from '@/pages/luxe-submissions-mm-data-overview'
import { useTranslation } from '@/translations/i18n'
import type { MMDataOverviewPipelineBreakdown } from '../types'

const PipelineKeys = z.enum([
	'preOnboarding',
	'backInReview',
	'readyForInspection',
	'failedStandards'
])

type PipelineKeys = z.infer<typeof PipelineKeys>

const PIPELINE_COLORS: Record<PipelineKeys, string> = {
	preOnboarding: '#EAECF0',
	failedStandards: '#F04438',
	readyForInspection: '#47CD89',
	backInReview: '#FEC84B'
}

const PIPELINE_I18N_KEYS = {
	preOnboarding: 'luxe_submissions:pipeline_breakdown_reasons.pre_onboarding',
	failedStandards:
		'luxe_submissions:pipeline_breakdown_reasons.failed_standards',
	readyForInspection:
		'luxe_submissions:pipeline_breakdown_reasons.ready_for_inspection',
	backInReview: 'luxe_submissions:pipeline_breakdown_reasons.back_in_review'
} as const satisfies Record<PipelineKeys, string>

interface MMOverviewPipelineBreakdownProps {
	data: MMDataOverviewPipelineBreakdown
}

function MMOverviewPipelineBreakdown({
	data
}: MMOverviewPipelineBreakdownProps) {
	const { t } = useTranslation()

	const total = PipelineKeys.options.reduce((sum, key) => sum + data[key], 0)

	return (
		<div className={cn(OVERVIEW_CARD_CLASSNAME, 'gap-6 px-8')}>
			<div className='flex flex-col gap-3 text-grey-1000'>
				<span className='font-semibold text-lg'>
					{t('luxe_submissions:pipeline_breakdown')}
				</span>

				<div className='flex items-baseline gap-2'>
					<span className='font-semibold text-lg'>{total}</span>
					<span className='text-sm'>
						{t('luxe_submissions:total_listings', {
							count: total
						})}
					</span>
				</div>
			</div>

			{total > 0 && (
				<div className='flex h-14 overflow-hidden rounded-lg'>
					{PipelineKeys.options.map((item) => (
						<div
							key={item}
							style={{
								width: `${(data[item] / total) * 100}%`,
								backgroundColor: PIPELINE_COLORS[item]
							}}
						/>
					))}
				</div>
			)}

			<div className='flex flex-col gap-4'>
				{PipelineKeys.options.map((item) => (
					<div
						className='flex items-center gap-2 text-neutral-600 text-sm'
						key={item}
					>
						<span
							className='size-2 shrink-0 rounded-full'
							style={{
								backgroundColor: PIPELINE_COLORS[item]
							}}
						/>
						<span className='font-bold'>
							{t(PIPELINE_I18N_KEYS[item])}
						</span>
						<span className='text-neutral-600 text-sm'>
							· {data[item]}{' '}
							{t('luxe_submissions:listing', {
								count: data[item]
							}).toLowerCase()}
						</span>
					</div>
				))}
			</div>
		</div>
	)
}

export { MMOverviewPipelineBreakdown }
